import React, { useRef, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import ReactSVG from 'react-svg';
import { useQuery, ApolloProvider } from 'react-apollo';
import { decode } from 'html-decoder';
import { useInView } from 'react-intersection-observer';
import chevron from 'chevron-small.svg';
import building from 'building.svg';
import { client } from '../client';
import useSlides from '../../hooks/useSlides';
import useWindowSize from '../../hooks/useWindowSize';
import ConnectApollo from '../ConnectApollo';
import LoadingSlider from '../Loading/LoadingSlider';
import RentalCard from '../RentalCard';
import EventCard from '../EventCard';

// Queries
import {
  propertiesQuery,
  eventsQuery,
  listingsQuery,
  communityPropertiesQuery,
} from '../../constants/queries';

import ListingCard from '../ListingCard';
import randomize from '../../utils/randomize';
import ErrorBoundary from '../ErrorBoundary';

const queries = {
  community_properties: communityPropertiesQuery,
  properties: propertiesQuery,
  events: eventsQuery,
  activities: listingsQuery,
  restaurants: listingsQuery,
  services: listingsQuery,
  shopping: listingsQuery,
};

const cardWidths = {
  events: 220, // width 200px + (padding 10px * 2) = 220px total
  properties: 293,
  community_properties: 293,
  activities: 293,
  shopping: 293,
  restaurants: 293,
  services: 293,
  community_properties_small: 224,
  properties_small: 224,
  activities_small: 224,
  shopping_small: 224,
  restaurants_small: 224,
  services_small: 224,
};

function HomeCardSectionContent({
  headerTitle,
  cardTypes,
  viewAllLink,
  is_small,
  filters = {},
}) {
  const cardsWrapperRef = useRef(null);

  const [randomizedCollection, setRandomizedCollection] = useState(null);

  const { data, loading } = useQuery(queries[cardTypes], {
    variables: {
      page: '1',
      ...filters,
      listing_city_name_cont_any: filters.area_in,
    },
    fetchPolicy: 'cache-and-network',
  });

  const isPremier = filters.is_premier_eq === true;
  let queryType = 'listings';
  if (
    cardTypes === 'community_properties' ||
    cardTypes === 'events' ||
    cardTypes === 'properties'
  )
    queryType = cardTypes;

  const { width: windowWidth } = useWindowSize();
  const isMobile = windowWidth <= 768;

  const CARD_WIDTH = cardWidths[`${cardTypes}${is_small ? '_small' : ''}`];

  // Add loading dependency to recalculate slideCount after fetching data
  const slideCount = useSlides(
    {
      sliderRef: cardsWrapperRef,
      cardWidth: CARD_WIDTH,
    },
    [loading]
  );

  useEffect(() => {
    if (data && data[queryType] && data[queryType].collection) {
      if (cardTypes === 'events' || isPremier) {
        setRandomizedCollection(data[queryType].collection);
      } else {
        setRandomizedCollection(randomize(data[queryType].collection));
      }
    }
  }, [cardTypes, data, isPremier, queryType]);

  // Return Loading Cards
  if (loading)
    return (
      <LoadingSlider
        headerTitle={headerTitle}
        cardWidth={CARD_WIDTH}
        sliderType={cardTypes === 'events' ? 'events' : ''}
        is_small={is_small}
      />
    );

  let totalCount = 0;
  if (data && data[queryType])
    totalCount =
      cardTypes === 'community_properties'
        ? data[queryType].meta.total_count
        : data[queryType].total_count;

  // Hide the premier rentals section on the community pages if there are no premier properties for that community
  const isCommunityPage = viewAllLink.includes('comm_id=');
  const hideCards =
    cardTypes === 'community_properties' &&
    totalCount < 1 &&
    headerTitle.includes('Premier Rentals in') &&
    isCommunityPage;

  if (hideCards) return null;

  const renderCards = cardData => {
    switch (queryType) {
      // This is the case for community properties
      case 'community_properties':
        return (
          <ConnectApollo key={cardData.id}>
            <RentalCard
              id={`${queryType}-card-${cardData.id}`}
              property={cardData}
              is_small={is_small}
            />
          </ConnectApollo>
        );

      case 'properties':
        return (
          <ConnectApollo key={cardData.id}>
            <RentalCard
              id={`${queryType}-card-${cardData.id}`}
              property={cardData}
              is_small={is_small}
            />
          </ConnectApollo>
        );

      case 'events':
        return (
          <EventCard
            key={cardData.id}
            id={`${queryType}-card-${cardData.id}`}
            event={cardData}
          />
        );

      case 'listings':
        return (
          <ListingCard
            key={cardData.id}
            listing={cardData}
            id={`${queryType}-card-${cardData.id}`}
            is_small={is_small}
          />
        );

      default:
        console.error(`No case for cardType of: ${queryType}`);
        break;
    }
  };

  // Return Data Cards
  return (
    <section className="card-section-container" ref={cardsWrapperRef}>
      <aside className="card-section-header">
        <div className="header-title-container">
          <ReactSVG src={building} className="header-icon" />
          <h2 className="header-title">
            <a
              href={viewAllLink}
              title={`View All ${headerTitle}`}
              name={`View All ${headerTitle}`}
              aria-label={`View All ${headerTitle}`}
              className="header-view-all"
            >
              {decode(headerTitle)}
            </a>
          </h2>
        </div>

        <a
          href={viewAllLink}
          title={`View All ${headerTitle}`}
          name={`View All ${headerTitle}`}
          aria-label={`View All ${headerTitle}`}
          className="header-view-all"
        >
          <span>View All ({totalCount}+)</span>
          <ReactSVG src={chevron} className="header-view-all-icon" alt="" />
        </a>
      </aside>

      <div className="cards-wrapper">
        <div
          className={`cards-container ${
            cardTypes === 'events' ? 'events' : ''
          } ${is_small ? 'is_small' : ''}`}
        >
          {!loading &&
            (randomizedCollection && randomizedCollection.length && data ? (
              randomizedCollection
                .slice(0, isMobile ? 6 : slideCount)
                .map(renderCards)
            ) : (
              <div className="slider-fallback">
                <p>No {decode(headerTitle.replace(/-/g, ' '))} Available</p>
              </div>
            ))}
          {!loading && isMobile && <div className="slider-end-padding" />}
        </div>
      </div>
    </section>
  );
}

function HomeCardSection({ cardTypes, is_small, headerTitle, ...props }) {
  const [ref, inView] = useInView({
    threshold: 0,
    triggerOnce: true,
  });

  const CARD_WIDTH = cardWidths[`${cardTypes}${is_small ? '_small' : ''}`];

  return (
    <div ref={ref}>
      <ApolloProvider client={client}>
        {inView ? (
          <HomeCardSectionContent
            headerTitle={headerTitle}
            cardTypes={cardTypes}
            is_small={is_small}
            {...props}
          />
        ) : (
          <LoadingSlider headerTitle={headerTitle} cardWidth={CARD_WIDTH} />
        )}
      </ApolloProvider>
    </div>
  );
}

HomeCardSectionContent.propTypes = {
  headerTitle: PropTypes.string.isRequired,
  cardTypes: PropTypes.oneOf([
    'properties',
    'community_properties',
    'events',
    'activities',
    'shopping',
    'restaurants',
    'services',
  ]).isRequired,
  viewAllLink: PropTypes.string.isRequired,
  is_small: PropTypes.bool,
  filters: PropTypes.object.isRequired,
};

export default function HomeCardSectionWrapper({ ...props }) {
  return (
    <ErrorBoundary>
      <HomeCardSection {...props} />
    </ErrorBoundary>
  );
}
